/**
 * Per-tab live-ness, and the rule for who may turn the browser-wide live flag off.
 *
 * ## The problem this exists for
 *
 * `liveMode` (playbackStore) and `sidebarState` (globalStore) are both `createSyncSignal`s, so both
 * are BROWSER-wide: a write in one window reaches every other. But the thing that decides to clear
 * `liveMode` — the count of live maps currently mounted — is a module-level variable, which is
 * per-TAB. A per-tab counter answering for a browser-wide flag is the whole defect:
 *
 *   - an ordinary dataset map opened in a SECOND window cleared live mode for a genuinely live page
 *     in the first;
 *   - a live map unmounting on the way to another live page in the SAME tab cleared the flag that
 *     page had just been given.
 *
 * Nothing on either path writes the per-page report Follow switches (`liveManeuverFollow` /
 * `livePerformanceFollow`), so the live report page kept showing Follow as LIVE while every gate it
 * has — the merge polls, the push handlers, the follow effects, all of which read `liveMode()` — was
 * dead. Only a reload recovered it. That is the "Live Maneuvers stops updating" report.
 *
 * ## What is deliberately NOT ported from Alinghi
 *
 * Alinghi answers "is this tab live?" by inference: `sidebarState() === 'live'` AND this tab holds
 * neither a dataset id nor a date, on the grounds that the flag is shared while the scope
 * (`sessionStorage`) is not, so the scope is the tie-breaker.
 *
 * **That inference is wrong in RaceSight** and must not be copied. Here the live report pages
 * legitimately write `selectedDate` (`LiveManeuvers` and `LivePerformance` both seed it with the
 * session day), so "has a date" does not mean "not live" — the test would read false on exactly the
 * pages it is meant to protect. RaceSight's own live-ness test is the disjunction
 * `sidebarState() === 'live' || liveMode()`, used in ~15 places in the Sidebar.
 *
 * So this module does not infer anything. It is TOLD, through `noteTabLive`, from the one choke point
 * every transition already goes through (`globalStore.setSidebarState`). A fact recorded at module
 * scope is per-tab by construction, which is the property the whole file is about.
 *
 * Known and left alone: because `sidebarState` is synced, another window entering live still flips
 * this tab's shared flag. Fixing that needs a per-tab scope discriminator RaceSight does not have,
 * and guessing one is what the paragraph above rejects.
 */

/**
 * Does THIS tab consider itself live?
 *
 * Module scope on purpose: no other window can rewrite it. Starts false — a fresh tab is not live
 * until something here says so, and every route into live mode goes through `setSidebarState('live')`
 * or mounts a live map.
 */
let tabIsLive = false;

/**
 * Record this tab's live-ness. Called from `globalStore.setSidebarState` (which covers all twelve
 * transition sites) and from `Sidebar.ensureLiveSidebarContext`, which is the one place that can
 * conclude "this tab is live" WITHOUT writing the setter — it returns early when the shared signal
 * already reads 'live'.
 */
export function noteTabLive(value: boolean): void {
  tabIsLive = value;
}

/**
 * How many LIVE map instances are mounted in THIS tab.
 *
 * Per-tab by construction, and that is the point: a mounted live map is a fact about this tab that no
 * other window can rewrite, so it is the right thing to ask before deciding this tab has no live
 * surface left.
 */
let liveMapsMountedInThisTab = 0;

export function noteLiveMapMounted(): void {
  liveMapsMountedInThisTab += 1;
}

export function noteLiveMapUnmounted(): void {
  liveMapsMountedInThisTab = Math.max(0, liveMapsMountedInThisTab - 1);
}

export function liveMapsMountedHere(): number {
  return liveMapsMountedInThisTab;
}

/**
 * May a map component clear the browser-wide `liveMode` flag?
 *
 * Both halves are required: nothing live on screen HERE, and this tab not in live scope. A map can
 * only ever answer for its own window, so without the second half an unmount in one place turns live
 * mode off for a live page somewhere else.
 *
 * @param liveMapsMounted how many LIVE map instances are mounted in this tab
 * @param tabIsLiveNow    this tab's own live-ness (`isLivePageActive`)
 */
export function mayClearGlobalLiveMode(liveMapsMounted: number, tabIsLiveNow: boolean): boolean {
  return liveMapsMounted <= 0 && !tabIsLiveNow;
}

/**
 * Is this tab showing a live surface?
 *
 * Replaces seven copies of `window.location.pathname.includes('/live/')`, every one of which was dead
 * code: RaceSight has **no** `/live` route at all (see the router in `App.tsx`), and the live pages —
 * live map, Live Maneuvers, Live Performance — are loaded by the sidebar's dynamic import while the
 * tab is in live mode, so their pathname is `/dashboard`. The test was therefore never true, and the
 * HuniDB flush handlers in `index.tsx` it was written to skip have been serializing the whole SQLite
 * image to IndexedDB, on the main thread, on every `visibilitychange` — while a live map animates.
 *
 * NOT `liveMode()` from playbackStore: that flag is browser-wide and sticky, so it reports live long
 * after this tab moved on, and would suppress flushes that should happen.
 */
export function isLivePageActive(): boolean {
  if (typeof window === 'undefined') { return false; }
  return tabIsLive || liveMapsMountedInThisTab > 0;
}
